import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { Zap, Calendar, FolderOpen, Flag, X, Loader2, Check } from "lucide-react";
import * as chrono from "chrono-node";

const PRIORITY_MAP = { "!p1": "critical", "!p2": "high", "!p3": "medium", "!p4": "low" };

function parseQuickCapture(text) {
  let title = text;
  let dueDate = null;
  let projectHint = null;
  let priority = "medium";

  // Extract #project
  const projectMatch = title.match(/#(\S+)/);
  if (projectMatch) {
    projectHint = projectMatch[1];
    title = title.replace(projectMatch[0], "").trim();
  }

  // Extract !priority
  for (const [token, prio] of Object.entries(PRIORITY_MAP)) {
    if (title.toLowerCase().includes(token)) {
      priority = prio;
      title = title.replace(new RegExp(token.replace("!", "\\!"), "i"), "").trim();
      break;
    }
  }

  // Parse date using chrono-node
  const parsed = chrono.parse(title);
  if (parsed.length > 0) {
    const result = parsed[0];
    dueDate = result.start.date().toISOString().slice(0, 10);
    // Remove the date text from title
    title = title.replace(result.text, "").trim();
  }

  // Clean up leftover whitespace
  title = title.replace(/\s+/g, " ").trim();

  return { title, dueDate, projectHint, priority };
}

export default function QuickCapture() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [parsed, setParsed] = useState(null);
  const [projects, setProjects] = useState([]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const { tokens: t } = useTheme();
  const inputRef = useRef(null);

  // Q key trigger (outside text inputs)
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.key === "q" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Listen for custom event from command palette
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("mmntm:quick-capture", handler);
    return () => window.removeEventListener("mmntm:quick-capture", handler);
  }, []);

  // Load projects when opened
  useEffect(() => {
    if (!open) { setInput(""); setParsed(null); setCreated(false); return; }
    api.getProjects().then(p => setProjects(p || [])).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Parse input live
  useEffect(() => {
    if (!input.trim()) { setParsed(null); return; }
    const result = parseQuickCapture(input);

    // Try to match project hint to actual project
    if (result.projectHint) {
      const match = projects.find(p =>
        p.name.toLowerCase().includes(result.projectHint.toLowerCase())
      );
      if (match) {
        result.projectId = match.id;
        result.projectName = match.name;
      }
    }

    setParsed(result);
  }, [input, projects]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!parsed?.title?.trim()) return;
    setCreating(true);
    try {
      const taskData = {
        title: parsed.title,
        priority: parsed.priority,
        category: parsed.projectId ? "project" : "daily",
      };
      if (parsed.dueDate) taskData.due_date = parsed.dueDate;
      if (parsed.projectId) taskData.project_id = parsed.projectId;

      const created = await api.createTask(taskData);
      track("task_created", { task_id: created?.id, has_project: !!created?.project_id, source: "manual" });
      setCreated(true);
      toast.success(`Task created: ${parsed.title}`);
      setTimeout(() => setOpen(false), 800);
    } catch {
      toast.error("Failed to create task");
    }
    setCreating(false);
  };

  if (!open) return null;

  const priorityColors = { critical: "#EF4444", high: "#F59E0B", medium: "#3B82F6", low: "#6B7280" };

  return (
    <div className="fixed inset-0 z-[185]" data-testid="quick-capture">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="flex items-start justify-center pt-[20vh]">
        <div className="relative w-full max-w-lg mx-4 rounded-xl shadow-2xl overflow-hidden mmntm-popup border"
          style={{ background: "var(--tm-bg-card)", borderColor: "var(--tm-accent)" }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5" style={{ color: "#F59E0B" }} />
              <span className="text-xs font-bold text-white/70">Quick Capture</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:opacity-70" aria-label="Close quick capture"
              data-testid="quick-capture-close">
              <X className="h-3.5 w-3.5 text-white/30" />
            </button>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit}>
            <div className="px-4 py-3">
              <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
                className="w-full text-sm bg-transparent outline-none text-white placeholder:text-white/25"
                placeholder="What needs to be done? (try: Review PR tomorrow #work !p1)"
                disabled={creating || created}
                data-testid="quick-capture-input"
                autoFocus />
            </div>

            {/* Live parsed preview */}
            {parsed && parsed.title && (
              <div className="px-4 pb-3 space-y-1.5" data-testid="quick-capture-preview">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/30 mb-1">Preview</div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-medium text-white">{parsed.title}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {parsed.dueDate && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium"
                      style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA", border: "1px solid rgba(59,130,246,0.2)" }}>
                      <Calendar className="h-2.5 w-2.5" /> {parsed.dueDate}
                    </span>
                  )}
                  {parsed.projectName && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium"
                      style={{ background: "rgba(168,85,247,0.12)", color: "#C084FC", border: "1px solid rgba(168,85,247,0.2)" }}>
                      <FolderOpen className="h-2.5 w-2.5" /> {parsed.projectName}
                    </span>
                  )}
                  {parsed.projectHint && !parsed.projectName && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium"
                      style={{ background: "rgba(245,158,11,0.12)", color: "#FBBF24", border: "1px solid rgba(245,158,11,0.2)" }}>
                      <FolderOpen className="h-2.5 w-2.5" /> #{parsed.projectHint} (no match)
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium"
                    style={{ background: `${priorityColors[parsed.priority]}15`, color: priorityColors[parsed.priority], border: `1px solid ${priorityColors[parsed.priority]}30` }}>
                    <Flag className="h-2.5 w-2.5" /> {parsed.priority}
                  </span>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <span className="text-[10px] text-white/30">
                <kbd className="px-1 py-0 text-[9px] rounded font-mono" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>↵</kbd> Create
                <span className="mx-2">·</span>
                <kbd className="px-1 py-0 text-[9px] rounded font-mono" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>Esc</kbd> Cancel
              </span>
              <button type="submit" disabled={creating || created || !parsed?.title?.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all disabled:opacity-30"
                style={{ background: created ? "#10B981" : "#A855F7", color: "#fff" }}
                data-testid="quick-capture-submit">
                {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : created ? <Check className="h-3 w-3" /> : <Zap className="h-3 w-3" />}
                {creating ? "Creating..." : created ? "Created!" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
